import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, MessageSquare, Plus, RefreshCw } from 'lucide-react'
import { searchCockpit, type CockpitSearchResult } from '@/lib/api'
import { cn } from '@/lib/utils'
import { compileQuery } from '../lib'
import { useCockpitData } from '../useCockpitData'
import { CockpitBoard } from './CockpitBoard'
import { CockpitSearchResults } from './CockpitSearchResults'
import { CaptureSheet } from './CaptureSheet'
import { CockpitTabBar } from './CockpitTabBar'
import { FileViewer, type ViewTarget } from './FileViewer'
import '../../concierge/components/concierge.css'

/** /cockpit — server-direct dashboard, the web port of the Obsidian plugin's
 * CockpitView Board tab (the Time tab is deliberately not ported). Standalone
 * layout (no AppShell), dark concierge palette, mobile-first. Items open in a
 * read-only FileViewer — editing happens via Concierge chat or Obsidian. */
export function CockpitPage() {
  const navigate = useNavigate()
  const { payload, loading, refreshing, error, refresh } = useCockpitData()
  const [query, setQuery] = useState('')
  const [domain, setDomain] = useState<string | null>(null)
  const [captureOpen, setCaptureOpen] = useState(false)
  const [viewTarget, setViewTarget] = useState<ViewTarget | null>(null)
  const [serverResults, setServerResults] = useState<CockpitSearchResult | null>(null)
  const [searching, setSearching] = useState(false)
  // Vault-wide quick matches while typing — debounced, exact-syntax only
  // (no semantic; that costs an embedding call per burst and is Enter's job).
  // Makes EVERY block and card searchable, not just the payload's capped rows.
  const [autoResults, setAutoResults] = useState<CockpitSearchResult | null>(null)
  const autoSeq = useRef(0)

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setAutoResults(null)
      return
    }
    const seq = ++autoSeq.current
    const t = window.setTimeout(() => {
      searchCockpit(q, false)
        .then((r) => {
          if (autoSeq.current === seq) setAutoResults(r)
        })
        .catch(() => {
          if (autoSeq.current === seq) setAutoResults(null)
        })
    }, 500)
    return () => window.clearTimeout(t)
  }, [query])

  // Client-side filter predicate — master.md query syntax, same as the
  // plugin's _matches (memoized compile per query string).
  const pred = useMemo(() => compileQuery(query), [query])
  const matches = useCallback(
    (...parts: Array<string | null | undefined>) => {
      if (!query.trim()) return true
      return pred(parts.filter(Boolean).join(' '))
    },
    [pred, query]
  )

  const openPath = useCallback((path: string, name?: string, meta?: string) => {
    setViewTarget({ path, name, meta })
  }, [])

  const runServerSearch = async () => {
    const q = query.trim()
    if (!q) {
      setServerResults(null)
      return
    }
    setSearching(true)
    try {
      setServerResults(await searchCockpit(q, true))
    } catch (e) {
      setServerResults({ ok: false, error: e instanceof Error ? e.message : String(e) })
    } finally {
      setSearching(false)
    }
  }

  const clearSearch = () => {
    setQuery('')
    setServerResults(null)
  }

  const freshness = payload
    ? `data ${new Date(payload.generated_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} · build ${(payload.build_ms / 1000).toFixed(1)}s${payload.live_overlay ? ' · live' : ''}${payload.stale_seconds ? ` · stale ${Math.round(payload.stale_seconds)}s` : ''}`
    : ''

  return (
    <div className="concierge-root h-dvh flex flex-col bg-background text-foreground">
      <header className="flex-shrink-0 border-b border-border">
        <div className="max-w-[760px] mx-auto px-5 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label="Back to home"
              className="-ml-1 p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-[15px] text-foreground">cockpit</div>
            <span className="text-[11px] text-muted-foreground truncate hidden sm:inline">
              {refreshing ? 'refreshing…' : freshness}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setCaptureOpen(true)}
              aria-label="Quick capture"
              title="Quick capture"
              className="hidden md:flex items-center justify-center px-2 py-1 border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus size={14} />
            </button>
            <button
              type="button"
              onClick={() => navigate('/concierge')}
              aria-label="Open chat"
              title="Concierge chat"
              className="hidden md:flex items-center justify-center px-2 py-1 border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              <MessageSquare size={14} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => void refresh(true)}
              aria-label="Refresh"
              title="Force a server rebuild"
              className="flex items-center justify-center px-2 py-1 border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              <RefreshCw size={14} strokeWidth={1.5} className={cn(refreshing && 'animate-spin')} />
            </button>
          </div>
        </div>
      </header>

      {/* Sticky controls: search + domain chips */}
      <div className="flex-shrink-0 border-b border-border">
        <div className="max-w-[760px] mx-auto px-5 py-2 space-y-2">
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              if (!e.target.value.trim()) setServerResults(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void runServerSearch()
              }
              if (e.key === 'Escape') clearSearch()
            }}
            placeholder='Search… (AND, OR, -not, *wild, "phrase" · Enter = deep search)'
            className="w-full px-3 py-1.5 text-base bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground"
          />
          {payload && payload.domains.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-0.5">
              <DomainChip label="all" active={domain === null} onClick={() => setDomain(null)} />
              {payload.domains.map((d) => (
                <DomainChip
                  key={d.name}
                  label={d.name}
                  active={domain === d.name}
                  onClick={() => setDomain(domain === d.name ? null : d.name)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <main className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-[760px] mx-auto px-5 py-4 pb-mobile-nav md:pb-8">
          {loading && <div className="text-[13px] text-muted-foreground py-8 text-center">Loading…</div>}
          {error && !payload && (
            <div className="text-[13px] text-red-400 py-8 text-center break-words">{error}</div>
          )}
          {payload &&
            (serverResults || searching ? (
              <CockpitSearchResults
                query={query}
                searching={searching}
                results={serverResults}
                domain={domain}
                onBack={() => setServerResults(null)}
                onOpen={openPath}
              />
            ) : (
              <>
                {query.trim().length >= 2 && (
                  <VaultWideMatches results={autoResults} domain={domain} onOpen={openPath} />
                )}
                <CockpitBoard
                  data={payload}
                  domain={domain}
                  setDomain={setDomain}
                  matches={matches}
                  onOpen={openPath}
                  refresh={() => void refresh(true)}
                />
              </>
            ))}
          {payload && error && (
            <div className="text-[11px] text-red-400 mt-4 break-words">Last refresh failed: {error}</div>
          )}
        </div>
      </main>

      <CockpitTabBar onCaptureCreated={() => void refresh(true)} />
      <CaptureSheet open={captureOpen} onClose={() => setCaptureOpen(false)} onCreated={() => void refresh(true)} />
      <FileViewer target={viewTarget} onClose={() => setViewTarget(null)} onNavigate={openPath} />
    </div>
  )
}

/** Vault-wide quick matches rendered above the filtered board while a query
 * is active. Exact-syntax over names + note bodies (server bytes) — covers
 * everything the payload's capped sections don't. Capped at 12 rows; press
 * Enter for the full results view with the semantic section. */
function VaultWideMatches({
  results,
  domain,
  onOpen,
}: {
  results: CockpitSearchResult | null
  domain: string | null
  onOpen: (path: string, name?: string, meta?: string) => void
}) {
  const all = (results?.exact || []).filter((r) => !domain || r.domain === domain)
  const rows = all.slice(0, 12)
  return (
    <section className="border border-border px-3 py-2 mb-4">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
        Vault-wide matches{results ? ` · ${all.length}` : ''}
        <span className="normal-case tracking-normal"> — Enter for grouped + semantic results</span>
      </div>
      {!results && <div className="text-[12px] text-muted-foreground italic">Searching…</div>}
      {results && !rows.length && <div className="text-[12px] text-muted-foreground italic">No matches.</div>}
      {rows.map((r) => (
        <div key={r.path} className="flex items-center gap-2.5 px-1 py-1 min-w-0 hover:bg-white/[0.04]">
          <span className="flex-shrink-0 text-[9px] border border-border px-1 text-muted-foreground uppercase">
            {r.kind}
          </span>
          <button
            type="button"
            className="cursor-pointer text-[13px] text-foreground truncate hover:underline text-left"
            onClick={() => onOpen(r.path, r.name, `${r.domain}${r.workstream ? ` / ${r.workstream}` : ''}`)}
          >
            {r.name}
          </button>
          <span className="ml-auto flex-shrink-0 text-[11px] text-muted-foreground truncate max-w-[40%]">
            {r.domain}
            {r.workstream ? ` / ${r.workstream}` : ''}
          </span>
        </div>
      ))}
    </section>
  )
}

function DomainChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-shrink-0 text-[11px] px-2.5 py-1 border rounded-full transition-colors',
        active ? 'border-foreground text-foreground' : 'border-border text-muted-foreground hover:text-foreground'
      )}
    >
      {label}
    </button>
  )
}
